import { useRef, useEffect, useCallback, type ReactNode } from "react"
import { useDrag } from "@use-gesture/react"
import { Capacitor } from "@capacitor/core"
import { useViewContext } from "@/app/stores/viewContext"
import { useAndroidBack } from "@/shared/hooks/useAndroidBack"
import { BACK_PRIORITY } from "@/shared/utils/platform/backHandlerStack"
import { cn } from "@/shared/utils/general/cn"

/** Width of the touch zone on the LEFT edge of the drawer that starts a close gesture */
const CLOSE_EDGE_ZONE = 30 // px
/** Width of the touch zone on the right edge that starts a drawer open gesture */
const EDGE_SWIPE_ZONE = 20 // px
/** Velocity threshold — fast swipe completes regardless of distance */
const VELOCITY_THRESHOLD = 0.5

interface MobileChatDrawerProps {
  children: ReactNode
}

/**
 * Full-screen swipeable chat drawer for mobile.
 * Mirrors the sidebar MobileDrawer but from the RIGHT edge.
 * Swipe from right edge to open, swipe right to close.
 */
export function MobileChatDrawer({ children }: MobileChatDrawerProps) {
  const isOpen = useViewContext((s) => s.aiChatOpen)
  const setAiChatOpen = useViewContext((s) => s.setAiChatOpen)
  const drawerRef = useRef<HTMLDivElement>(null)
  const backdropRef = useRef<HTMLDivElement>(null)
  const isGesturing = useRef(false)

  // Android back button → close chat
  const handleBack = useCallback(() => {
    setAiChatOpen(false)
    return true
  }, [setAiChatOpen])
  useAndroidBack(BACK_PRIORITY.SIDEBAR + 1, isOpen, handleBack, "ai-chat")

  // Track gesture translate X for direct manipulation during drag
  const translateX = useRef(0)
  const closeAbort = useRef<AbortController | null>(null)

  function setDrawerVisible(visible: boolean) {
    if (drawerRef.current) {
      drawerRef.current.style.visibility = visible ? "visible" : "hidden"
    }
  }

  // Apply transform directly for 60fps (no state re-renders during drag)
  function applyTransform(x: number) {
    if (drawerRef.current) {
      drawerRef.current.style.transform = `translateX(${x}px)`
      drawerRef.current.style.transition = "none"
    }
    if (backdropRef.current) {
      const vw = window.innerWidth
      // x goes from vw (closed) to 0 (open). Progress: 1 when open, 0 when closed.
      const progress = Math.max(0, 1 - x / vw)
      backdropRef.current.style.opacity = String(progress * 0.5)
      backdropRef.current.style.transition = "none"
    }
  }

  // Snap to open or closed with CSS transition
  function snapTo(open: boolean) {
    if (closeAbort.current) {
      closeAbort.current.abort()
      closeAbort.current = null
    }

    if (open) {
      setDrawerVisible(true)
    }
    if (drawerRef.current) {
      drawerRef.current.style.transition =
        "transform 300ms cubic-bezier(0.22, 1.0, 0.36, 1)"
      drawerRef.current.style.transform = open
        ? "translateX(0)"
        : "translateX(100%)"
      if (!open) {
        const abort = new AbortController()
        closeAbort.current = abort
        drawerRef.current.addEventListener(
          "transitionend",
          () => {
            if (!abort.signal.aborted) {
              setDrawerVisible(false)
            }
          },
          { once: true, signal: abort.signal },
        )
      }
    }
    if (backdropRef.current) {
      backdropRef.current.style.transition =
        "opacity 300ms cubic-bezier(0.22, 1.0, 0.36, 1)"
      backdropRef.current.style.opacity = open ? "0.5" : "0"
    }
  }

  // Sync with React state changes
  useEffect(() => {
    snapTo(isOpen)
  }, [isOpen])

  // Sync status bar style when chat drawer opens/closes (same pattern as MobileDrawer).
  // FocusBanner forces Style.Dark (white icons); the chat drawer covers it with a light bg.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return

    let cancelled = false
    void (async () => {
      try {
        const { StatusBar, Style } = await import("@capacitor/status-bar")
        if (cancelled) return
        const isDark = document.documentElement.classList.contains("dark")
        if (isOpen) {
          await StatusBar.setStyle({ style: isDark ? Style.Dark : Style.Light })
        } else {
          const { isStatusBannerActive } = await import("@/app/components/AppStatusBanner")
          if (cancelled) return
          if (isStatusBannerActive()) {
            await StatusBar.setStyle({ style: Style.Dark })
          } else {
            await StatusBar.setStyle({ style: isDark ? Style.Dark : Style.Light })
          }
        }
      } catch {
        // Plugin not available
      }
    })()

    return () => { cancelled = true }
  }, [isOpen])

  // Close on Escape
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape" && isOpen) {
        setAiChatOpen(false)
      }
    }
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [isOpen, setAiChatOpen])

  // Gesture on the drawer panel itself (for closing by swiping right from left edge)
  const closeStartedRef = useRef(false)
  const bindDrawer = useDrag(
    ({ xy: [startX], movement: [mx], velocity: [vx], direction: [dx], active, first }) => {
      // Only start close gesture from the left edge of the drawer
      if (first) {
        closeStartedRef.current = startX <= CLOSE_EDGE_ZONE
      }
      if (!closeStartedRef.current) return

      // Only allow swiping right (to close)
      if (mx < 0) {
        applyTransform(0)
        return
      }

      if (active) {
        applyTransform(mx)
        translateX.current = mx
      } else {
        // Released — decide snap
        const shouldClose = mx > 100 || (vx > VELOCITY_THRESHOLD && dx > 0)
        if (shouldClose) {
          setAiChatOpen(false)
        } else {
          snapTo(true)
        }
        translateX.current = 0
        closeStartedRef.current = false
      }
    },
    { axis: "x", filterTaps: true },
  )

  // Edge-swipe gesture on the content area (for opening from right edge)
  const bindEdge = useDrag(
    ({
      xy: [startX],
      movement: [mx],
      velocity: [vx],
      direction: [dx],
      active,
      first,
    }) => {
      // Only start from the right edge
      if (first && startX < window.innerWidth - EDGE_SWIPE_ZONE) {
        return
      }

      // Make drawer visible on first frame of gesture
      if (first) {
        isGesturing.current = true
        setDrawerVisible(true)
      }

      if (active) {
        // Map gesture to drawer position: starts at 100vw (off-screen right), moves toward 0
        // mx is negative when swiping left (opening)
        const vw = window.innerWidth
        const drawerX = Math.max(0, vw + mx)
        applyTransform(drawerX)
      } else {
        isGesturing.current = false
        // Released — decide snap (mx is negative when opening, so check absolute)
        const shouldOpen = mx < -100 || (vx > VELOCITY_THRESHOLD && dx < 0)
        if (shouldOpen) {
          setAiChatOpen(true)
        } else {
          snapTo(false)
        }
      }
    },
    { axis: "x", filterTaps: true },
  )

  return (
    <>
      {/* Backdrop */}
      <div
        ref={backdropRef}
        className={cn(
          "fixed inset-0 z-40 bg-black",
          isOpen ? "pointer-events-auto" : "pointer-events-none",
        )}
        style={{ opacity: 0 }}
        onClick={() => setAiChatOpen(false)}
        aria-hidden="true"
      />

      {/* Drawer panel — slides in from right */}
      <div
        ref={drawerRef}
        {...bindDrawer()}
        className="fixed inset-y-0 right-0 z-50 w-full bg-background shadow-xl"
        style={{
          transform: "translateX(100%)",
          visibility: "hidden",
        }}
        role={isOpen ? "dialog" : undefined}
        aria-modal={isOpen ? "true" : undefined}
        aria-label="AI Chat"
      >
        {children}
      </div>

      {/* Invisible edge swipe zone on right — only render when drawer is closed */}
      {!isOpen && (
        <div
          {...bindEdge()}
          className="fixed inset-y-0 right-0 z-30"
          style={{ width: EDGE_SWIPE_ZONE }}
          aria-hidden="true"
        />
      )}
    </>
  )
}
